import { memo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { fromDateTime, verifyTemporaryAccessPin } from '@/services/ws-companies/src/main/resources/rest-api';
import { TextField, useForm } from '@/js/libs/form';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Button, Modal, useNotification } from '@/js/libs/ui/src/V2';
import { useLazyRequest } from '@/js/libs/use-request';

import { getErrorMessage } from 'src/hooks/useRequestErrorNotification.ts';
import { ErrorCode } from 'src/types.ts';
import { PIN_FIELD_NAME, PIN_LENGTH } from './constants.ts';
import { useVerifyPinFormSchema, type FormValues, type SubmitValues } from './schema.ts';
import type { VerifyPinModalProps } from './types.ts';

export const VerifyPinModal = memo(function VerifyPinModal({ state, companyId, companyName, onConfirmed }: VerifyPinModalProps) {
  const [t] = useTranslation(['admin', 'common']);

  const notify = useNotification();

  const schema = useVerifyPinFormSchema();
  const form = useForm<FormValues, unknown, SubmitValues>({
    defaultValues: { [PIN_FIELD_NAME]: '' },
    resolver: zodResolver(schema),
    mode: 'onChange',
  });
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = form;

  const verifyRequest = useLazyRequest({
    apiMethod: verifyTemporaryAccessPin,
    // Verifies the PIN as the logged-in admin: the endpoint requires ROLE_ADMIN, which a selected company scopes down to USER_ADMIN.
    callUnderLoggedUser: true,
    ignoreErrors: [ErrorCode.Forbidden],
    onFail: (e) => notify.error({ title: t('common:ERROR'), body: getErrorMessage(e.response, t) }),
  });

  const onSubmit = useCallbackRef(async (values: SubmitValues) => {
    try {
      const response = await verifyRequest.load({ companyId, form: { pin: values.pin } });
      const expiresAtMs = fromDateTime(response.data.expiresAt).valueOf();
      onConfirmed(expiresAtMs);
    } catch {
      // the failure is surfaced by the request's onFail notification
    }
  });

  return (
    <Modal state={state} title={t('admin:LOG_IN_TO_COMPANY', { companyName })}>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            <TextField
              intOnly
              required
              control={control}
              name={PIN_FIELD_NAME}
              label={t('admin:ENTER_THE_CODE')}
              maxLength={PIN_LENGTH}
              inputMode='numeric'
              disabled={isSubmitting}
              dataTestId='temporary-access-pin-input'
            />
          </Modal.Body>
          <Modal.Footer>
            <Button ghost onClick={state.close} disabled={isSubmitting} dataTestId='temporary-access-cancel'>
              {t('common:CANCEL')}
            </Button>
            <Button primary type='submit' isLoading={isSubmitting} disabled={isSubmitting} dataTestId='temporary-access-verify'>
              {t('common:LOG_IN')}
            </Button>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  );
});
